import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { AtomicLoadingScreen } from '../ui/atomic-loader';
import { toast } from '../ui/use-toast';
import { 
  MessageSquare, 
  Plus, 
  Edit2, 
  Trash2, 
  Shield, 
  Zap, 
  Eye, 
  Save,
  X,
  Copy,
  RefreshCw,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Settings,
  EyeOff,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import api from '@/lib/api';

interface WhatsAppConnection {
  id: number;
  name: string;
  connection_type: 'oficial' | 'extra_oficial';
  is_active: boolean;
  client_id: number;
}

/** Config e templates ÁtomosChat (Envios V2) */
interface AtomosChatConfigState {
  envioVersion: string;
  config: { account_name?: string; channel_name?: string } | null;
}
interface AtomosChatTemplateItem {
  id: string;
  name: string;
  text?: string;
  status: string;
  categoryName?: string;
  /** Quando "DOCUMENT", o template usa anexo; deve-se mapear a coluna de origem (ex.: link_boleto). */
  fileType?: string;
  /** Parâmetros explícitos (p1, p2, ...) quando a API retorna */
  parameters?: { name?: string; [key: string]: unknown }[];
}

interface MetaApiTemplate {
  id: string;
  name: string;
  category?: string;
  language: string;
  status: string;
  components: {
    type: string;
    text?: string;
    format?: string;
    example?: any;
    buttons?: any[];
  }[];
  parameter_format: string;
  display_format?: string;
  sub_category?: string;
}

interface TemplateVariableMapping {
  id: number;
  client_id: number;
  template_name: string;
  variable_index: number;
  database_column: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface MetaApiTemplatesTabProps {
  clientId: number;
}

// Função para gerar saudação baseada no horário de Brasília
const getSaudacao = (): string => {
  const now = new Date();
  const horaBrasilia = new Date(now.toLocaleString("en-US", {timeZone: "America/Sao_Paulo"}));
  const hora = horaBrasilia.getHours();
  
  if (hora >= 5 && hora < 12) {
    return 'Bom dia';
  } else if (hora >= 12 && hora < 18) {
    return 'Boa tarde';
  } else {
    return 'Boa noite';
  }
};

/** connection_id usado para mapeamentos ÁtomosChat (Envios V2) na tabela template_variable_mappings */
const ATOMOS_CHAT_CONNECTION_ID = 0;

/** Mapeamento coluna -> nome da variável na API AtomosChat (templates com placeholders Placa, Modelo, etc.) */
const COLUMN_TO_ATOMOS_VARIABLE_NAME: Record<string, string> = {
  saudacao: 'saudacao',
  nome_associado: 'nome_associado',
  placa: 'Placa',
  modelo_veiculo: 'Modelo',
  data_vencimento: 'Vencimento',
  vencimento_boas_vindas: 'Vencimento',
  valor_boleto: 'Valor',
  data_pagamento: 'Data Pagamento',
  valor_pagamento: 'valor_pagamento',
};

// Colunas disponíveis da tabela disparos_boletos (ordenadas alfabeticamente)
const DISPAROS_BOLETOS_COLUMNS = [
  { value: 'beneficios', label: 'Benefícios (Hinova)' },
  { value: 'celular', label: 'Celular' },
  { value: 'data_pagamento', label: 'Data Pagamento' },
  { value: 'data_vencimento', label: 'Data de Vencimento' },
  { value: 'endereco', label: 'Endereço' },
  { value: 'linha_digitavel', label: 'Linha Digitável' },
  { value: 'link_boleto', label: 'Link do Boleto' },
  { value: 'modelo_veiculo', label: 'Modelo do Veículo' },
  { value: 'nome_associado', label: 'Nome do Associado' },
  // { value: 'nosso_numero', label: 'Nosso Número' }, // REMOVIDO
  { value: 'pix_copia_cola', label: 'PIX Copia e Cola' },
  { value: 'placa', label: 'Placa do Veículo' },
  { value: 'saudacao', label: 'Saudação' },
  { value: 'valor_boleto', label: 'Valor do Boleto' },
  { value: 'vencimento_boas_vindas', label: 'Vencimento (Boas-vindas)' }
];



// Dados de exemplo para preview
const SAMPLE_BOLETO_DATA = {
  nome_associado: 'João Silva',
  placa: 'ABC1234',
  modelo_veiculo: 'CIVIC LXS',
  data_vencimento: '2025-06-20',
  data_pagamento: '2025-03-04',
  valor_boleto: 100.00,
  celular: '(11) 99999-9999',
  pix_copia_cola: '00020126580014BR.GOV.BCB.PIX0136123e4567-e12b-12d1-a456-4266141740005204000053039865405100.005802BR5913Modelo APV6008São Paulo62070503***6304E2CA',
  linha_digitavel: '23793.38128 60000.000000 12345.678901 8 84410026000010000',
  link_boleto: 'https://boleto.example.com/123456789',
  beneficios: '• Chave reserva\n• Veículo extra',
  // nosso_numero: '123456789', // REMOVIDO
  codigo_veiculo: 'VEI001',
  codigo_associado: 'ASS001',
  codigo_regional: 'REG001',
  cliente_nome_fantasia: 'Modelo APV',
  cliente_cnpj: '12.345.678/0001-90',
  saudacao: getSaudacao()
};

// Função para verificar se um template tem mapeamentos salvos
const checkIfTemplateMapped = (templateName: string, mappings: { [templateName: string]: any }): boolean => {
  return mappings[templateName] && Object.keys(mappings[templateName]).length > 0;
};

// Função para verificar se um template foi salvo localmente
const checkIfTemplateSavedLocally = (templateName: string, savedTemplates: { [templateName: string]: boolean }): boolean => {
  const isSaved = savedTemplates[templateName] === true;
  console.log(`🔍 [MetaApiTemplatesTab] Verificando se template "${templateName}" foi salvo:`, {
    templateName,
    isSaved,
    savedTemplates,
    hasTemplate: templateName in savedTemplates,
    templateValue: savedTemplates[templateName]
  });
  return isSaved;
};

export function MetaApiTemplatesTab({ clientId }: MetaApiTemplatesTabProps) {
  console.log('🚀 [MetaApiTemplatesTab] COMPONENTE MONTADO - clientId:', clientId);
  
  const [connections, setConnections] = useState<WhatsAppConnection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<WhatsAppConnection | null>(null);
  const [templates, setTemplates] = useState<MetaApiTemplate[]>([]);
  const [templateMappings, setTemplateMappings] = useState<{ [templateName: string]: any }>({});
  const [savedTemplates, setSavedTemplates] = useState<{ [templateName: string]: boolean }>({});
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<MetaApiTemplate | null>(null);
  // ÁtomosChat (Envios V2): suporte a múltiplas conexões V2 por cliente
  const [atomosChatConfig, setAtomosChatConfig] = useState<AtomosChatConfigState | null>(null);
  const [atomosChatConfigs, setAtomosChatConfigs] = useState<{ id: number; account_name: string; channel_name?: string | null; channel_phone?: string | null; is_active?: boolean }[]>([]);
  const [selectedAtomosConfigId, setSelectedAtomosConfigId] = useState<number | null>(null);
  const [atomosTemplates, setAtomosTemplates] = useState<AtomosChatTemplateItem[]>([]);
  const [loadingAtomosTemplates, setLoadingAtomosTemplates] = useState(false);
  const [variableMappings, setVariableMappings] = useState<{ [key: string]: string }>({});
  const [previewContent, setPreviewContent] = useState<{
    header: string;
    body: string;
    footer: string;
    buttons: string[];
  }>({
    header: '',
    body: '',
    footer: '',
    buttons: []
  });
  
  // Estados para modais
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  // ÁtomosChat V2: modal e estado de mapeamento de variáveis
  const [selectedAtomosTemplate, setSelectedAtomosTemplate] = useState<AtomosChatTemplateItem | null>(null);
  const [templateMappingsAtomos, setTemplateMappingsAtomos] = useState<{ [templateName: string]: { [variable: string]: string } }>({});
  const [showEditModalAtomos, setShowEditModalAtomos] = useState(false);
  const [variableMappingsAtomos, setVariableMappingsAtomos] = useState<{ [key: string]: string }>({});
  /** Coluna em disparos_boletos usada como URL do documento (quando template.fileType === 'DOCUMENT'). */
  const [documentColumnAtomos, setDocumentColumnAtomos] = useState<string>('link_boleto');
  const [previewBodyAtomos, setPreviewBodyAtomos] = useState('');

  useEffect(() => {
    // Resetar estados quando clientId muda para evitar cache de dados de outro cliente
    setConnections([]);
    setSelectedConnection(null);
    setTemplates([]);
    setSelectedTemplate(null);
    setVariableMappings({});
    setPreviewContent({
      header: '',
      body: '',
      footer: '',
      buttons: []
    });
    setShowPreviewModal(false);
    setShowEditModal(false);
    setAtomosChatConfig(null);
    setAtomosChatConfigs([]);
    setSelectedAtomosConfigId(null);
    setAtomosTemplates([]);

    loadConnections();
  }, [clientId]);

  // Recarrega templates V2 ao trocar a config V2 escolhida
  useEffect(() => {
    if (selectedAtomosConfigId != null) {
      loadAtomosTemplates();
    }
  }, [selectedAtomosConfigId]);

  useEffect(() => {
    if (selectedConnection) {
      loadTemplates(selectedConnection.id);
      loadSavedTemplates(selectedConnection.id);
    }
  }, [selectedConnection]);

  const loadConnections = async () => {
    try {
      setLoading(true);
      console.log('🔍 [MetaApiTemplatesTab] Carregando conexões para clientId:', clientId);
      // Carregar WhatsApp (Meta) + lista de configs V2 + preferência V1/V2 em paralelo
      const [whatsappRes, configsRes, versionRes] = await Promise.all([
        api.get(`/whatsapp/client/${clientId}`).catch(() => ({ connections: [] })) as Promise<any>,
        api.get(`/atomos-chat/client/${clientId}/configs`).catch(() => null) as Promise<{ success?: boolean; configs?: any[] } | null>,
        api.get(`/atomos-chat/client/${clientId}/envio-version`).catch(() => null) as Promise<{ success?: boolean; envio_version?: 'v1' | 'v2' } | null>,
      ]);

      let response: any = whatsappRes;
      console.log('🔍 [MetaApiTemplatesTab] Resposta completa da API WhatsApp:', response);
      const v2Configs = Array.isArray(configsRes?.configs) ? configsRes!.configs! : [];
      const activeV2 = v2Configs.filter((c: any) => c.is_active !== false);
      setAtomosChatConfigs(activeV2 as any);
      console.log('🔍 [MetaApiTemplatesTab] Configs V2 ativas:', activeV2.length);
      if (activeV2.length > 0 && versionRes?.envio_version === 'v2') {
        // Mantém o estado legado de "single config" para o JSX existente
        const first = activeV2[0];
        setAtomosChatConfig({ envioVersion: 'v2', config: { account_name: first.account_name, channel_name: first.channel_name } });
        if (selectedAtomosConfigId == null) setSelectedAtomosConfigId(first.id);
      } else {
        setAtomosChatConfig(null);
      }

      const resolveConnections = (payload: any): WhatsAppConnection[] => {
        if (!payload) return [];
        if (Array.isArray(payload)) return payload as WhatsAppConnection[];
        if (Array.isArray(payload.connections)) return payload.connections as WhatsAppConnection[];
        if (payload.data && Array.isArray(payload.data.connections)) return payload.data.connections as WhatsAppConnection[];
        return [] as WhatsAppConnection[];
      };

      let allConnections = resolveConnections(response);
      if (allConnections.length === 0) {
        console.warn('⚠️ [MetaApiTemplatesTab] Fallback: /whatsapp/client/current');
        const current = await api.get(`/whatsapp/client/current`);
        allConnections = resolveConnections(current);
      }

      const officialConnections = allConnections.filter((conn: WhatsAppConnection) =>
        conn.connection_type === 'oficial'
      );
      console.log('🔍 [MetaApiTemplatesTab] Conexões oficiais filtradas:', officialConnections);

      setConnections(officialConnections);
      
      if (officialConnections.length > 0) {
        // 🔧 CORREÇÃO: Priorizar conexões ATIVAS primeiro
        const activeConnections = officialConnections.filter((conn: WhatsAppConnection) => conn.is_active);
        
        console.log('🔍 [MetaApiTemplatesTab] Conexões oficiais:', officialConnections.length);
        console.log('🔍 [MetaApiTemplatesTab] Conexões oficiais ATIVAS:', activeConnections.length);
        
        // Se houver múltiplas conexões ativas, escolher a primeira ATIVA
        const connectionToUse = activeConnections.length > 0 ? activeConnections[0] : officialConnections[0];
        
        console.log('🔍 [MetaApiTemplatesTab] Conexão selecionada:', {
          id: connectionToUse.id,
          name: connectionToUse.name,
          is_active: connectionToUse.is_active
        });
        
        setSelectedConnection(connectionToUse);
        
        // Se houver múltiplas conexões, avisar no console
        if (officialConnections.length > 1) {
          console.warn(`⚠️ [MetaApiTemplatesTab] Cliente tem ${officialConnections.length} conexões oficiais. Usando: ${connectionToUse.name} (ID: ${connectionToUse.id})`);
          console.log('💡 [MetaApiTemplatesTab] DICA: Adicione um seletor para alternar entre conexões no futuro');
        }
      } else {
        console.log('⚠️ [MetaApiTemplatesTab] Nenhuma conexão oficial ativa encontrada');
      }
    } catch (error) {
      console.error('❌ [MetaApiTemplatesTab] Erro ao carregar conexões:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar conexões da API Meta",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAtomosTemplates = async (overrideConfigId?: number | null) => {
    if (!clientId) return;
    try {
      setLoadingAtomosTemplates(true);
      const cfgId = overrideConfigId ?? selectedAtomosConfigId;
      const url = cfgId
        ? `/atomos-chat/client/${clientId}/templates?config_id=${cfgId}`
        : `/atomos-chat/client/${clientId}/templates`;
      const data = await api.get(url) as { success: boolean; templates: AtomosChatTemplateItem[]; count: number };
      const list = data.templates || [];
      setAtomosTemplates(list);
      await loadAtomosTemplateMappings(list);
      toast({
        title: "Templates carregados",
        description: `${list.length} template(s) da ÁtomosChat (Envios V2).`,
      });
    } catch (e: any) {
      console.error('[MetaApiTemplatesTab] loadAtomosTemplates:', e);
      toast({
        title: "Erro ao carregar templates",
        description: e?.message ?? "Verifique o token na aba API de Envio (Envios V2).",
        variant: "destructive",
      });
      setAtomosTemplates([]);
      setTemplateMappingsAtomos({});
    } finally {
      setLoadingAtomosTemplates(false);
    }
  };

  /** Carrega mapeamentos de variáveis dos templates ÁtomosChat (V2, connection_id=0) */
  const loadAtomosTemplateMappings = async (templatesList: AtomosChatTemplateItem[]) => {
    if (!clientId || templatesList.length === 0) {
      setTemplateMappingsAtomos({});
      return;
    }
    try {
      const mappings: { [templateName: string]: { [variable: string]: string } } = {};
      for (const t of templatesList) {
        try {
          const response = await api.get(
            `/template-variable-mappings/template/${encodeURIComponent(t.name)}?client_id=${clientId}&connection_id=${ATOMOS_CHAT_CONNECTION_ID}`
          ) as { success?: boolean; mappings?: { variable_index: number; variable_name?: string | null; database_column: string }[] };
          if (response.success && response.mappings && response.mappings.length > 0) {
            const obj: { [variable: string]: string } = {};
            response.mappings.forEach((m: { variable_index: number; variable_name?: string | null; database_column: string }) => {
              const pKey = `p${m.variable_index}`;
              obj[pKey] = m.database_column;
              if (m.variable_name) obj[m.variable_name] = m.database_column;
            });
            mappings[t.name] = obj;
          }
        } catch {
          // template sem mapeamentos
        }
      }
      setTemplateMappingsAtomos(mappings);
    } catch (error) {
      console.error('[MetaApiTemplatesTab] loadAtomosTemplateMappings:', error);
      setTemplateMappingsAtomos({});
    }
  };

  const loadTemplates = async (connectionId: number) => {
    try {
      setLoading(true);
      console.log('🔍 [MetaApiTemplatesTab] Carregando templates para connectionId:', connectionId);
      const response = await api.get(`/whatsapp/${connectionId}/templates`);
      console.log('🔍 [MetaApiTemplatesTab] Templates carregados:', response);
      setTemplates(response.templates || []);
      
      // Carregar mapeamentos existentes para cada template (desta conexão Meta)
      await loadTemplateMappings(response.templates || [], connectionId);
    } catch (error: any) {
      console.error('❌ [MetaApiTemplatesTab] Erro ao carregar templates:', error);
      
      // 🔧 CORREÇÃO: Mensagem de erro mais detalhada para debug
      const errorMessage = error.response?.data?.error || error.message || "Erro desconhecido";
      const connectionName = connections.find(c => c.id === connectionId)?.name || `ID ${connectionId}`;
      
      console.error(`❌ [MetaApiTemplatesTab] Detalhes do erro:`, {
        connectionId,
        connectionName,
        status: error.response?.status,
        errorMessage,
        fullError: error
      });
      
      toast({
        title: "Erro ao carregar templates",
        description: `Conexão "${connectionName}": ${errorMessage}. Verifique se a conexão está ativa e o token é válido.`,
        variant: "destructive"
      });
      
      // Limpar templates em caso de erro
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateMappings = async (templates: MetaApiTemplate[], connectionId?: number | null) => {
    try {
      console.log('🔍 [MetaApiTemplatesTab] INICIANDO carregamento de mapeamentos para', templates.length, 'templates, connection_id:', connectionId);
      const mappings: { [templateName: string]: any } = {};
      const connectionParam = connectionId !== undefined && connectionId !== null ? `&connection_id=${connectionId}` : '';
      
      for (const template of templates) {
        try {
          console.log(`🔍 [MetaApiTemplatesTab] Buscando mapeamentos para template: ${template.name}, cliente: ${clientId}`);
          const response = await api.get(`/template-variable-mappings/template/${template.name}?client_id=${clientId}${connectionParam}`);
          console.log(`📊 [MetaApiTemplatesTab] Resposta para ${template.name}:`, response);
          
          if (response.success && response.mappings && response.mappings.length > 0) {
            // Converter array de mapeamentos para objeto
            const templateMappingObj: { [variable: string]: string } = {};
            response.mappings.forEach((mapping: any) => {
              templateMappingObj[`{{${mapping.variable_index}}}`] = mapping.database_column;
            });
            mappings[template.name] = templateMappingObj;
            console.log(`✅ [MetaApiTemplatesTab] Template ${template.name} tem mapeamentos:`, templateMappingObj);
          } else {
            console.log(`⚠️ [MetaApiTemplatesTab] Template ${template.name} sem mapeamentos - resposta:`, response);
          }
        } catch (error) {
          console.log(`⚠️ [MetaApiTemplatesTab] Template ${template.name} sem mapeamentos salvos - erro:`, error);
        }
      }
      
      setTemplateMappings(mappings);
      console.log('🎯 [MetaApiTemplatesTab] FINALIZADO - Todos os mapeamentos carregados:', mappings);
      console.log('🎯 [MetaApiTemplatesTab] Templates com mapeamentos:', Object.keys(mappings));
    } catch (error) {
      console.error('❌ [MetaApiTemplatesTab] Erro ao carregar mapeamentos:', error);
    }
  };

  const loadSavedTemplates = async (connectionId: number) => {
    try {
      console.log('🔍 [MetaApiTemplatesTab] Carregando templates salvos localmente para conexão:', connectionId);
      const response = await api.get(`/meta-api/connections/${connectionId}/templates/local`);
      
      console.log('📊 [MetaApiTemplatesTab] Resposta da API para templates salvos:', response);
      
      if (response.success && response.templates) {
        const savedTemplatesMap: { [templateName: string]: boolean } = {};
        response.templates.forEach((template: any) => {
          savedTemplatesMap[template.name] = true;
          console.log(`📝 [MetaApiTemplatesTab] Template "${template.name}" marcado como salvo`);
        });
        
        console.log('🔄 [MetaApiTemplatesTab] Atualizando estado savedTemplates com:', savedTemplatesMap);
        setSavedTemplates(savedTemplatesMap);
        console.log('✅ [MetaApiTemplatesTab] Templates salvos localmente carregados:', savedTemplatesMap);
        console.log('📊 [MetaApiTemplatesTab] Total de templates salvos:', Object.keys(savedTemplatesMap).length);
        
        // Verificar se o estado foi atualizado corretamente
        setTimeout(() => {
          console.log('🔍 [MetaApiTemplatesTab] Verificação pós-atualização do estado savedTemplates:', savedTemplatesMap);
        }, 100);
      } else {
        console.log('⚠️ [MetaApiTemplatesTab] Resposta da API não contém templates ou success=false');
        setSavedTemplates({});
      }
    } catch (error) {
      console.log('⚠️ [MetaApiTemplatesTab] Erro ao carregar templates salvos:', error);
      setSavedTemplates({});
    }
  };

  const handleSyncTemplates = async () => {
    if (!selectedConnection) return;

    try {
      setSyncing(true);
      const response = await api.post(`/whatsapp/${selectedConnection.id}/templates/sync`);
      
      toast({
        title: "Sincronização Concluída",
        description: response.message || `${response.count} templates sincronizados`
      });

      loadTemplates(selectedConnection.id);
    } catch (error: any) {
      console.error('Erro ao sincronizar templates:', error);
      
      // Tratar erros específicos
      if (error.response?.status === 401) {
        toast({
          title: "Token Inválido",
          description: "Token de acesso inválido ou expirado. Atualize sua conexão com a API Meta.",
          variant: "destructive"
        });
      } else if (error.response?.status === 403) {
        toast({
          title: "Acesso Negado",
          description: "Acesso negado. Verifique as permissões do token.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Erro na Sincronização",
          description: error.response?.data?.error || error.message || "Erro ao sincronizar templates",
          variant: "destructive"
        });
      }
    } finally {
      setSyncing(false);
    }
  };

  const extractVariables = (template: MetaApiTemplate): string[] => {
    const variables: string[] = [];
    
    template.components.forEach(component => {
      if (component.text) {
        const matches = component.text.match(/\{\{\d+\}\}/g) || [];
        variables.push(...matches);
      }
    });
    
    return [...new Set(variables)].sort();
  };

  /** Variáveis no formato ÁtomosChat: p1, p2, ... ou [1], [2], ou semânticas [NOME], [PLACA], etc. */
  const extractVariablesForAtomos = (template: AtomosChatTemplateItem): string[] => {
    // Priorizar parâmetros explícitos da API (p1, p2, ...)
    if (template.parameters && Array.isArray(template.parameters) && template.parameters.length > 0) {
      const fromApi = template.parameters
        .map((p) => (typeof p.name === 'string' ? p.name : null))
        .filter((n): n is string => !!n && /^p\d+$/.test(n));
      if (fromApi.length > 0) {
        return [...new Set(fromApi)].sort((a, b) => parseInt(a.slice(1), 10) - parseInt(b.slice(1), 10));
      }
    }
    const text = template.text || '';
    // Detectar variáveis semânticas [NOME], [PLACA], [SAUDAÇÃO], [VALOR], [VENCIMENTO], [MODELO], etc.
    const bracketMatches = text.match(/\[([^\]]+)\]/g) || [];
    const semanticVars = [...new Set(
      bracketMatches
        .map((m) => m.slice(1, -1))
        .filter((name) => !/^p?\d+$/.test(name))
    )];
    if (semanticVars.length > 0) {
      return semanticVars;
    }
    // Fallback: p1, p2, [p1], [1], [2]
    const pBracketVars = [...new Set(text.match(/\[p\d+\]/g) || [])].map((m) => m.slice(1, -1));
    const pVars = [...new Set([...pBracketVars, ...(text.match(/\bp\d+\b/g) || [])])];
    const bracketVars = [...new Set(text.match(/\[\d+\]/g) || [])];
    const all = pVars.length > 0 ? pVars : bracketVars;
    return all.sort((a, b) => {
      const nA = a.startsWith('p') ? parseInt(a.slice(1), 10) : parseInt(a.replace(/[\[\]]/g, ''), 10);
      const nB = b.startsWith('p') ? parseInt(b.slice(1), 10) : parseInt(b.replace(/[\[\]]/g, ''), 10);
      return nA - nB;
    });
  };

  /** Preview do corpo substituindo p1, [p1], [1], [2] ou variáveis semânticas [NOME], [PLACA], etc. */
  const generatePreviewAtomos = (text: string, mappings: { [key: string]: string }): string => {
    let body = text;
    Object.entries(mappings).forEach(([variable, column]) => {
      const value = SAMPLE_BOLETO_DATA[column as keyof typeof SAMPLE_BOLETO_DATA];
      if (value !== undefined) {
        const strVal = String(value);
        const escaped = variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const isIndexed = variable.startsWith('p') || /^\d+$/.test(variable);
        // Variáveis indexadas (p1, p2): substituir [p1] e p1
        if (isIndexed) {
          body = body.replace(new RegExp(`\\[${escaped}\\]`, 'g'), strVal);
          body = body.replace(new RegExp(`\\b${escaped}\\b`, 'g'), strVal);
        } else {
          // Variáveis semânticas [NOME], [PLACA], etc.: substituir [VARIAVEL]
          body = body.replace(new RegExp(`\\[${escaped}\\]`, 'g'), strVal);
        }
      }
    });
    return body;
  };

  const generatePreview = (template: MetaApiTemplate, mappings: { [key: string]: string }) => {
    const previewData = {
      header: '',
      body: '',
      footer: '',
      buttons: [] as string[]
    };
    
    template.components.forEach(component => {
      if (component.type === 'BODY' && component.text) {
        let bodyText = component.text;
        
        // Aplicar mapeamentos
        Object.entries(mappings).forEach(([variable, column]) => {
          const value = SAMPLE_BOLETO_DATA[column as keyof typeof SAMPLE_BOLETO_DATA];
          if (value !== undefined) {
            bodyText = bodyText.replace(new RegExp(variable, 'g'), String(value));
          }
        });
        
        previewData.body = bodyText;
      } else if (component.type === 'FOOTER' && component.text) {
        previewData.footer = component.text;
      } else if (component.type === 'BUTTONS' && component.buttons) {
        previewData.buttons = component.buttons.map((button: any) => button.text || 'Botão');
      }
    });
    
    return previewData;
  };

  const handlePreviewTemplate = (template: MetaApiTemplate) => {
    setSelectedTemplate(template);
      const variables = extractVariables(template);
      const initialMappings: { [key: string]: string } = {};
      
      variables.forEach(variable => {
        initialMappings[variable] = '';
      });
      
      setVariableMappings(initialMappings);
      const preview = generatePreview(template, initialMappings);
      setPreviewContent(preview);
    setShowPreviewModal(true);
  };

  const handleEditTemplate = (template: MetaApiTemplate) => {
    setSelectedTemplate(template);
    const variables = extractVariables(template);
    const initialMappings: { [key: string]: string } = {};
    
    console.log('🔍 [MetaApiTemplatesTab] Abrindo edição do template:', template.name);
    
    variables.forEach(variable => {
      // Sempre deixar vazio para mostrar placeholder
      initialMappings[variable] = '';
    });
    
    setVariableMappings(initialMappings);
    
    // Gerar preview inicial vazio
    const preview = generatePreview(template, initialMappings);
    setPreviewContent(preview);
    
    setShowEditModal(true);
  };

  const handleMappingChange = (variable: string, column: string) => {
    const newMappings = { ...variableMappings, [variable]: column };
    setVariableMappings(newMappings);
    
    if (selectedTemplate) {
      const preview = generatePreview(selectedTemplate, newMappings);
      setPreviewContent(preview);
    }
  };

  const saveVariableMappings = async () => {
    if (!selectedTemplate) return;

    try {
      setLoading(true);
      
      // Incluir TODOS os mapeamentos válidos (incluindo campos virtuais)
      const validMappings = Object.entries(variableMappings)
        .filter(([_, column]) => column !== '' && column !== 'none')
        .map(([variable, column], index) => ({
          client_id: clientId,
          template_name: selectedTemplate.name,
          variable_index: parseInt(variable.replace(/[{}]/g, '')),
          database_column: column,
          connection_id: selectedConnection?.id, // 🆕 NOVO: Incluir connection_id
          is_active: true
        }));

      const payload = {
        client_id: clientId,
        template_name: selectedTemplate.name,
        mappings: validMappings
      };

      console.log('🔍 [MetaApiTemplatesTab] Payload sendo enviado:', JSON.stringify(payload, null, 2));
      console.log('🔍 [MetaApiTemplatesTab] Mappings filtrados (incluindo campos virtuais):', validMappings);

      await api.post('/template-variable-mappings/batch', payload);

      toast({
        title: "Mapeamentos Salvos",
        description: "Mapeamentos de variáveis salvos com sucesso"
      });

      // Recarregar os mapeamentos para atualizar a interface
      if (selectedConnection) {
        console.log('🔄 [MetaApiTemplatesTab] Recarregando mapeamentos após salvar...');
        await loadTemplateMappings(templates, selectedConnection?.id ?? undefined);
      }

      setShowEditModal(false);
    } catch (error) {
      console.error('❌ [MetaApiTemplatesTab] Erro ao salvar mapeamentos:', error);
      console.error('❌ [MetaApiTemplatesTab] Error response:', error.response?.data);
      toast({
        title: "Erro",
        description: "Erro ao salvar mapeamentos de variáveis",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditAtomosTemplate = async (template: AtomosChatTemplateItem) => {
    setSelectedAtomosTemplate(template);
    const variables = extractVariablesForAtomos(template);
    const existing = templateMappingsAtomos[template.name] || {};
    const initialMappings: { [key: string]: string } = {};
    variables.forEach((v) => {
      // API pode ter: p1, p2 (numéricos) ou SAUDACAO, Placa (semânticos). Template usa [1], [2] ou [SAUDACAO], [PLACA].
      const pKey = /^\[\d+\]$/.test(v) ? `p${v.replace(/[\[\]]/g, '')}` : /^\d+$/.test(v) ? `p${v}` : null;
      const keysToTry = [v, pKey].filter(Boolean);
      if (pKey == null && v) {
        keysToTry.push(v.toLowerCase(), v.toUpperCase(), v.charAt(0).toUpperCase() + v.slice(1).toLowerCase());
      }
      initialMappings[v] = keysToTry.map((k) => existing[k]).find((val) => val != null && val !== '') ?? '';
    });
    setVariableMappingsAtomos(initialMappings);
    setPreviewBodyAtomos(generatePreviewAtomos(template.text || '', initialMappings));
    setShowEditModalAtomos(true);
    if (template.fileType === 'DOCUMENT') {
      try {
        const res = await api.get(`/strategy-configurations/v2-document-column?client_id=${clientId}&template_name=${encodeURIComponent(template.name)}`) as { success?: boolean; atomos_file_column?: string };
        setDocumentColumnAtomos(res?.atomos_file_column ?? 'link_boleto');
      } catch {
        setDocumentColumnAtomos('link_boleto');
      }
    }
  };

  const handleMappingChangeAtomos = (variable: string, column: string) => {
    const newMappings = { ...variableMappingsAtomos, [variable]: column };
    setVariableMappingsAtomos(newMappings);
    if (selectedAtomosTemplate) {
      setPreviewBodyAtomos(generatePreviewAtomos(selectedAtomosTemplate.text || '', newMappings));
    }
  };

  const saveVariableMappingsAtomos = async () => {
    if (!selectedAtomosTemplate) return;
    try {
      setLoading(true);
      const validMappings = Object.entries(variableMappingsAtomos)
        .filter(([, column]) => column !== '' && column !== 'none')
        .map(([variable, column], index) => {
          const isSemantic = /^[A-Za-zÀ-ÿ_]+$/.test(variable) && !variable.startsWith('p');
          // Garantir variable_name: variáveis semânticas usam o nome; p1,p2,p3 usam mapeamento coluna->API
          const variableName = isSemantic
            ? variable
            : (COLUMN_TO_ATOMOS_VARIABLE_NAME[column] ?? null);
          return {
            client_id: clientId,
            template_name: selectedAtomosTemplate.name,
            variable_index: variable.startsWith('p') ? parseInt(variable.slice(1), 10) : parseInt(variable.replace(/[\[\]]/g, ''), 10) || index + 1,
            variable_name: variableName,
            database_column: column,
            connection_id: ATOMOS_CHAT_CONNECTION_ID,
            is_active: true,
          };
        });
      const payload = {
        client_id: clientId,
        template_name: selectedAtomosTemplate.name,
        mappings: validMappings
      };
      await api.post('/template-variable-mappings/batch', payload);
      if (selectedAtomosTemplate.fileType === 'DOCUMENT') {
        await api.patch('/strategy-configurations/v2-document-column', {
          client_id: clientId,
          template_name: selectedAtomosTemplate.name,
          atomos_file_column: documentColumnAtomos || 'link_boleto',
        });
      }
      toast({
        title: "Mapeamentos salvos",
        description: selectedAtomosTemplate.fileType === 'DOCUMENT'
          ? "Mapeamentos de variáveis (V2) e origem do documento salvos com sucesso"
          : "Mapeamentos de variáveis (V2) salvos com sucesso",
      });
      await loadAtomosTemplateMappings(atomosTemplates);
      setShowEditModalAtomos(false);
    } catch (error: any) {
      console.error('[MetaApiTemplatesTab] saveVariableMappingsAtomos:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.error || "Erro ao salvar mapeamentos de variáveis (V2)",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplateLocally = async (template: MetaApiTemplate) => {
    if (!selectedConnection) {
      toast({
        title: "Erro",
        description: "Nenhuma conexão selecionada",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);
      
      // Extrair componentes do template
      const headerComponent = template.components?.find(c => c.type === 'HEADER');
      const bodyComponent = template.components?.find(c => c.type === 'BODY');
      const footerComponent = template.components?.find(c => c.type === 'FOOTER');
      const buttonsComponent = template.components?.find(c => c.type === 'BUTTONS');

      const templateData = {
        meta_api_connection_id: selectedConnection.id,
        connection_type: 'whatsapp', // Apontar para whatsapp_connections
        meta_template_id: template.id,
        name: template.name,
        category: template.category,
        language: template.language,
        status: template.status,
        header_text: headerComponent?.text || null,
        body_text: bodyComponent?.text || '',
        footer_text: footerComponent?.text || null,
        buttons: buttonsComponent ? JSON.stringify(buttonsComponent) : null
      };

      console.log('🔍 [MetaApiTemplatesTab] Salvando template localmente:', templateData);

      const saveResponse = await api.post('/meta-api/templates/save-local', templateData);
      console.log('📊 [MetaApiTemplatesTab] Resposta do salvamento:', saveResponse);

      // Recarregar templates salvos para garantir consistência
      if (selectedConnection) {
        console.log('🔄 [MetaApiTemplatesTab] Recarregando templates salvos após salvamento...');
        await loadSavedTemplates(selectedConnection.id);
        console.log('✅ [MetaApiTemplatesTab] Templates salvos recarregados');
        
        // Verificar se o template foi realmente salvo
        console.log('🔍 [MetaApiTemplatesTab] Verificando se o template foi salvo...');
        const verificationResponse = await api.get(`/meta-api/connections/${selectedConnection.id}/templates/local`);
        console.log('🔍 [MetaApiTemplatesTab] Verificação de templates salvos:', verificationResponse);
      }

      toast({
        title: "Template Salvo",
        description: `Template "${template.name}" salvo localmente com sucesso`
      });

    } catch (error: any) {
      console.error('❌ [MetaApiTemplatesTab] Erro ao salvar template localmente:', error);
      
      if (error.response?.status === 409) {
        toast({
          title: "Template Já Existe",
          description: "Este template já foi salvo localmente anteriormente",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Erro",
          description: error.response?.data?.error || "Erro ao salvar template localmente",
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toUpperCase()) {
      case 'APPROVED':
        return <Badge className="bg-green-100 text-green-800">Aprovado</Badge>;
      case 'PENDING':
        return <Badge className="bg-yellow-100 text-yellow-800">Pendente</Badge>;
      case 'REJECTED':
        return <Badge className="bg-red-100 text-red-800">Rejeitado</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getCategoryBadge = (category?: string) => {
    if (!category) return null;
    
    switch (category.toUpperCase()) {
      case 'MARKETING':
        return <Badge className="bg-blue-100 text-blue-800">Marketing</Badge>;
      case 'UTILITY':
        return <Badge className="bg-purple-100 text-purple-800">Utilitário</Badge>;
      default:
        return <Badge variant="outline">{category}</Badge>;
    }
  };

  const renderTemplateContent = (template: MetaApiTemplate) => {
    let content = '';
    
    template.components.forEach(component => {
      if (component.type === 'BODY' && component.text) {
        content += `${component.text}\n\n`;
      } else if (component.type === 'FOOTER' && component.text) {
        content += `---\n${component.text}\n`;
      }
    });
    
    return content.trim();
  };

  if (loading && connections.length === 0) {
    return (
      <AtomicLoadingScreen 
        message="Carregando conexões da API Meta..." 
        className="h-32"
      />
    );
  }

  // Sem conexão Meta: se tiver Envios V2 (ÁtomosChat), mostrar seção de templates V2
  if (connections.length === 0 && atomosChatConfig?.envioVersion === 'v2') {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="w-5 h-5" />
              Templates ÁtomosChat (Envios V2)
            </CardTitle>
            <CardDescription>
              Este cliente usa Envios V2 (ÁtomosChat). Carregue os templates da API ÁtomosChat abaixo.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <Label className="text-sm">Conexão V2 (ÁtomosChat)</Label>
                <Select
                  value={selectedAtomosConfigId ? String(selectedAtomosConfigId) : ''}
                  onValueChange={(v) => {
                    const id = v ? Number(v) : null;
                    setSelectedAtomosConfigId(id);
                    setAtomosTemplates([]);
                    if (id != null) {
                      const cfg = atomosChatConfigs.find((c) => c.id === id);
                      if (cfg) {
                        setAtomosChatConfig({ envioVersion: 'v2', config: { account_name: cfg.account_name, channel_name: cfg.channel_name ?? undefined } });
                      }
                    }
                  }}
                  disabled={atomosChatConfigs.length === 0}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecione a conexão V2" />
                  </SelectTrigger>
                  <SelectContent>
                    {atomosChatConfigs.map((cfg) => (
                      <SelectItem key={cfg.id} value={String(cfg.id)}>
                        {cfg.account_name}{cfg.channel_phone ? ` — ${cfg.channel_phone}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {atomosChatConfigs.length > 1 && (
                  <p className="text-xs text-gray-500 mt-1">
                    O cliente tem {atomosChatConfigs.length} conexões V2. Os templates listados são da conta da conexão escolhida.
                  </p>
                )}
              </div>
              <Button
                onClick={() => loadAtomosTemplates()}
                disabled={loadingAtomosTemplates || !selectedAtomosConfigId}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {loadingAtomosTemplates ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Carregar templates
              </Button>
            </div>
            {atomosTemplates.length > 0 && (
              <div className="mt-6">
                <h4 className="font-semibold text-gray-900 mb-3">Templates ({atomosTemplates.length})</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {atomosTemplates.map((t) => (
                    <Card key={t.id} className="border border-gray-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-sm">{t.name}</h4>
                          <Badge variant="secondary">{t.status}</Badge>
                        </div>
                        {t.categoryName && (
                          <p className="text-xs text-gray-500 mb-2">{t.categoryName}</p>
                        )}
                        {t.text && (
                          <p className="text-xs text-gray-600 bg-gray-50 p-2 rounded max-h-24 overflow-y-auto">
                            {t.text.replace(/\n/g, ' ').slice(0, 120)}
                            {(t.text.length || 0) > 120 ? '…' : ''}
                          </p>
                        )}
                        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                          {templateMappingsAtomos[t.name] && Object.keys(templateMappingsAtomos[t.name]).length > 0 && (
                            <p className="text-xs text-green-600 font-medium">Mapeado</p>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditAtomosTemplate(t)}
                            className="ml-auto"
                          >
                            <Edit2 className="w-4 h-4 mr-2" />
                            Editar mapeamentos
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal Editar mapeamentos - ÁtomosChat (V2) */}
        {showEditModalAtomos && selectedAtomosTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Editar mapeamentos - ÁtomosChat - {selectedAtomosTemplate.name}</h3>
                <Button variant="ghost" size="sm" onClick={() => setShowEditModalAtomos(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h5 className="font-medium text-gray-900 flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Mapeamento de variáveis (V2)
                  </h5>
                  <div className="space-y-3">
                    {extractVariablesForAtomos(selectedAtomosTemplate).map((variable) => (
                      <div key={variable} className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs min-w-[60px] justify-center">{variable}</Badge>
                        <span className="text-gray-500">→</span>
                        <Select
                          value={variableMappingsAtomos[variable] || ''}
                          onValueChange={(value) => handleMappingChangeAtomos(variable, value)}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Selecione a coluna" />
                          </SelectTrigger>
                          <SelectContent>
                            {DISPAROS_BOLETOS_COLUMNS.map((col) => (
                              <SelectItem key={col.value} value={col.value}>{col.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                  {selectedAtomosTemplate.fileType === 'DOCUMENT' && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h6 className="text-sm font-medium text-gray-700 mb-2">Origem do documento (anexo)</h6>
                      <p className="text-xs text-gray-500 mb-2">
                        Este template usa anexo (DOCUMENT). Defina de qual coluna da base será carregada a URL do arquivo.
                      </p>
                      <Select
                        value={documentColumnAtomos || 'link_boleto'}
                        onValueChange={setDocumentColumnAtomos}
                      >
                        <SelectTrigger className="w-full max-w-xs">
                          <SelectValue placeholder="Coluna para URL do documento" />
                        </SelectTrigger>
                        <SelectContent>
                          {DISPAROS_BOLETOS_COLUMNS.map((col) => (
                            <SelectItem key={col.value} value={col.value}>{col.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div className="space-y-4">
                  <h5 className="font-medium text-gray-900 flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Preview da mensagem
                  </h5>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 min-h-[200px] max-h-[300px] overflow-y-auto">
                    <div className="flex justify-end mb-4">
                      <div className="bg-green-500 rounded-lg p-3 max-w-[80%] shadow-sm relative">
                        <div className="text-sm text-white whitespace-pre-wrap">{previewBodyAtomos || 'Configure as variáveis para ver o preview...'}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setShowEditModalAtomos(false)}>Cancelar</Button>
                <Button onClick={saveVariableMappingsAtomos} disabled={loading}>
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Salvar mapeamentos
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (connections.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExternalLink className="w-5 h-5" />
            Templates da API Meta
          </CardTitle>
          <CardDescription>
            Gerencie os templates da API do Meta (Facebook/WhatsApp) ou da ÁtomosChat (Envios V2)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Nenhuma conexão da API Meta configurada</p>
            <p className="text-sm">Configure uma conexão na aba API de Envio (Meta ou Envios V2 - ÁtomosChat)</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* 🔧 NOVO: Seletor de Conexão (quando há múltiplas conexões) */}
      {connections.length > 1 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Conexão WhatsApp:
              </label>
              <select
                value={selectedConnection?.id || ''}
                onChange={(e) => {
                  const conn = connections.find(c => c.id === parseInt(e.target.value));
                  if (conn) {
                    console.log('🔄 [MetaApiTemplatesTab] Conexão alterada para:', conn.name);
                    setSelectedConnection(conn);
                  }
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {connections.map(conn => (
                  <option key={conn.id} value={conn.id}>
                    {conn.name} {conn.is_active ? '✓ Ativa' : '⚠ Inativa'} (ID: {conn.id})
                  </option>
                ))}
              </select>
              <span className="text-xs text-gray-500">
                {connections.length} conexões disponíveis
              </span>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Header com título e botão de sincronização */}
          <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Templates da Meta ({templates.length})</h2>
          <p className="text-gray-600 mt-1">
            Templates aprovados da API Oficial Meta para {selectedConnection?.name || 'esta conexão'} WhatsApp.
          </p>
        </div>
            <Button
              onClick={handleSyncTemplates}
              disabled={syncing || !selectedConnection}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
            >
              {syncing ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sincronizar Templates
            </Button>
          </div>
          
      {/* Grid de Templates */}
          {selectedConnection && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
            <div className="col-span-full">
              <AtomicLoadingScreen 
                message="Carregando templates..." 
                className="h-32"
              />
            </div>
            ) : templates.length === 0 ? (
            <div className="col-span-full text-center py-8 text-gray-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>Nenhum template encontrado</p>
                <p className="text-sm">Clique em "Sincronizar Templates" para buscar da API Meta</p>
              </div>
            ) : (
            templates.map((template) => (
                    <Card key={template.id} className="border border-gray-200 hover:border-blue-300 transition-colors">
                      <CardContent className="p-4">
                        {/* Header do Template */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <h4 className="font-semibold text-sm">{template.name}</h4>
                            <div className="flex gap-1">
                              {getStatusBadge(template.status)}
                              {getCategoryBadge(template.category)}
                            </div>
                          </div>
                        </div>
                        
                        {/* Conteúdo do Template */}
                  <div className="space-y-2 text-sm mb-4">
                          <div>
                            <span className="font-medium text-gray-600">Conteúdo:</span>
                            <p className="text-gray-800 bg-gray-50 p-2 rounded text-xs max-h-20 overflow-y-auto">
                              {renderTemplateContent(template)}
                            </p>
                          </div>
                        </div>
                        
                        {/* Informações do Template */}
                  <div className="mb-4 pt-3 border-t border-gray-100">
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>ID: {template.id}</span>
                            <span>{template.language}</span>
                          </div>
                          {checkIfTemplateMapped(template.name, templateMappings) && (
                            <p className="text-xs text-green-600 font-medium mt-1">Template Mapeado</p>
                          )}
                          {(() => {
                            const isSaved = checkIfTemplateSavedLocally(template.name, savedTemplates);
                            console.log(`🎨 [MetaApiTemplatesTab] Renderizando template "${template.name}":`, {
                              isSaved,
                              savedTemplates,
                              willRender: isSaved
                            });
                            return isSaved ? (
                              <p className="text-xs text-blue-600 font-medium mt-1">Template Salvo</p>
                            ) : null;
                          })()}
                        </div>

                  {/* Botões de Ação */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePreviewTemplate(template)}
                      className="flex-1"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Visualizar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditTemplate(template)}
                      className="flex-1"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSaveTemplateLocally(template)}
                      className="flex-1"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Salvar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Modal de Preview */}
      {showPreviewModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Preview do Template</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPreviewModal(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              {/* WhatsApp Chat Style */}
              <div className="flex justify-end mb-4">
                <div className="bg-green-500 rounded-lg p-3 max-w-[80%] shadow-sm relative">
                  {/* Tail da bolha */}
                  <div className="absolute -right-2 top-3 w-0 h-0 border-l-8 border-l-green-500 border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>
                  
                  <div className="text-sm text-white space-y-2">
                    {previewContent.header && (
                      <div className="font-semibold text-white">
                        {previewContent.header}
                      </div>
                    )}
                    {previewContent.body && (
                      <div className="whitespace-pre-wrap text-white">
                        {previewContent.body}
                      </div>
                    )}
                    {previewContent.footer && (
                      <div className="text-xs text-green-100 pt-2 border-t border-green-400">
                        {previewContent.footer}
                      </div>
                    )}
                    {previewContent.buttons.length > 0 && (
                      <div className="space-y-2 pt-2">
                        {previewContent.buttons.map((button, index) => (
                          <button
                            key={index}
                            className="w-full bg-white bg-opacity-20 border border-white border-opacity-30 rounded-lg px-3 py-2 text-sm text-white hover:bg-white hover:bg-opacity-30 transition-colors flex items-center justify-between backdrop-blur-sm"
                          >
                            <span>{button}</span>
                            <span className="text-white text-opacity-70">↗</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-green-100 text-right mt-1 flex items-center justify-end gap-1">
                    <span>16:48</span>
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end mt-4">
              <Button
                variant="outline"
                onClick={() => setShowPreviewModal(false)}
              >
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição */}
      {showEditModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Editar Template - {selectedTemplate.name}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEditModal(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              {/* Mapeamento de Variáveis (apenas edição) */}
                              <div className="space-y-4">
                                <h5 className="font-medium text-gray-900 flex items-center gap-2">
                                  <Settings className="w-4 h-4" />
                                  Mapeamento de Variáveis
                                </h5>
                                
                                <div className="space-y-3">
                  {extractVariables(selectedTemplate).map((variable) => (
                                    <div key={variable} className="flex items-center gap-2">
                                      <Badge variant="outline" className="text-xs min-w-[60px] justify-center">
                                        {variable}
                                      </Badge>
                                      <span className="text-gray-500">→</span>
                                      <Select
                                        value={variableMappings[variable] || ''}
                                        onValueChange={(value) => handleMappingChange(variable, value)}
                                      >
                                        <SelectTrigger className="flex-1">
                                          <SelectValue placeholder="Selecione a variável" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {DISPAROS_BOLETOS_COLUMNS.map((col) => (
                                            <SelectItem key={col.value} value={col.value}>
                                              {col.label}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  ))}
                                </div>
                              </div>

                                                             {/* Preview da Mensagem */}
                               <div className="space-y-4">
                                 <h5 className="font-medium text-gray-900 flex items-center gap-2">
                                   <Eye className="w-4 h-4" />
                                   Preview da Mensagem
                                 </h5>
                                                                   <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 min-h-[200px] max-h-[300px] overflow-y-auto">
                                    {/* WhatsApp Chat Style */}
                                    <div className="flex justify-end mb-4">
                                      <div className="bg-green-500 rounded-lg p-3 max-w-[80%] shadow-sm relative">
                                        {/* Tail da bolha */}
                                        <div className="absolute -right-2 top-3 w-0 h-0 border-l-8 border-l-green-500 border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>
                                        
                                        <div className="text-sm text-white space-y-2">
                                          {previewContent.header && (
                                            <div className="font-semibold text-white">
                                              {previewContent.header}
                                            </div>
                                          )}
                                          {previewContent.body && (
                                            <div className="whitespace-pre-wrap text-white">
                                              {previewContent.body}
                                            </div>
                                          )}
                                          {previewContent.footer && (
                                            <div className="text-xs text-green-100 pt-2 border-t border-green-400">
                                              {previewContent.footer}
                                            </div>
                                          )}
                                          {previewContent.buttons.length > 0 && (
                                            <div className="space-y-2 pt-2">
                                              {previewContent.buttons.map((button, index) => (
                                                <button
                                                  key={index}
                                                  className="w-full bg-white bg-opacity-20 border border-white border-opacity-30 rounded-lg px-3 py-2 text-sm text-white hover:bg-white hover:bg-opacity-30 transition-colors flex items-center justify-between backdrop-blur-sm"
                                                >
                                                  <span>{button}</span>
                                                  <span className="text-white text-opacity-70">↗</span>
                                                </button>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                        <div className="text-xs text-green-100 text-right mt-1 flex items-center justify-end gap-1">
                                          <span>16:48</span>
                                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                        </div>
                                      </div>
                                    </div>
                                    {!previewContent.body && (
                                      <div className="text-center text-gray-500 text-sm">
                                        Configure as variáveis para ver o preview...
                                      </div>
                                    )}
                                  </div>
                               </div>
                            </div>
                            
                            {/* Mapeamentos atuais do template (abaixo do preview) */}
                            {selectedTemplate && templateMappings[selectedTemplate.name] && Object.keys(templateMappings[selectedTemplate.name]).length > 0 && (
                              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center gap-2 mb-3">
                                  <CheckCircle className="h-5 w-5 text-green-600" />
                                  <span className="text-base font-medium text-green-800">Mapeamentos atuais do template:</span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                  {Object.entries(templateMappings[selectedTemplate.name]).map(([variable, field]) => (
                                    <div key={variable} className="bg-white p-3 rounded border border-green-200 flex items-center">
                                      <span className="font-mono font-bold text-blue-600 text-sm">{variable}</span> 
                                      <span className="mx-2 text-gray-400">→</span> 
                                      <span className="text-green-700 font-medium text-sm">
                                        {DISPAROS_BOLETOS_COLUMNS.find(col => col.value === field)?.label || field}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
            
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowEditModal(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={saveVariableMappings}
                disabled={loading}
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Salvar Mapeamentos
              </Button>
                            </div>
                          </div>
              </div>
      )}
    </div>
  );
}
